import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";
import TableContainer from "Components/Common/TableContainer";
import {
  getContractors,
  deleteContractor,
} from "common/services/contractors.service";
import { Flagged, SEMGMention } from "./ContractorsCol";
import Permission from "Components/Common/Permission";
import { getSortObj } from "common/lib/tableDataOptions";
import { parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import * as moment from "moment";
import DeleteModal from "Components/Common/DeleteModal";
import SelectOrganizationTypeFilter from "./SelectOrganizationTypeFilter";
import { normalize } from "common/lib/normalize";
import Import from "./Import";
import Permissions from "Components/Common/Permissions";
import BulkActionBar from "Components/Common/BulkActionBar";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import { isAllowed } from "common/lib/permissionUtils";
import { useProfile } from "Components/Hooks/UserHooks";
import {
  FilterLabel,
  FilterSearchBox,
  FilterValueSelect,
} from "Components/Common/TableFilters";

interface FilterValue {
  organizationType: string;
  flaggedStatus: string;
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT = [{ id: "createdAt", desc: true }];

const parseFilters = (searchParams: URLSearchParams): FilterValue => {
  const filters: FilterValue = {
    organizationType: searchParams.get("type") || "",
    flaggedStatus:
      searchParams.get("status")?.toLowerCase() === "flagged" ? "Flagged" : "",
  };

  // Continue to support links created before the compact URL format.
  const legacyFilter = searchParams.get("filter");
  if (legacyFilter && !filters.organizationType && !filters.flaggedStatus) {
    try {
      return { ...filters, ...JSON.parse(legacyFilter) };
    } catch {
      return filters;
    }
  }

  return filters;
};

const AllContractors = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || DEFAULT_PAGE_SIZE,
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, DEFAULT_SORT)
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [deleteModal, setDeleteModal] = useState(false);

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));
  const [filterValue, setFilterValue] = useState(() =>
    parseFilters(searchParams)
  );

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = new URLSearchParams();

      if (page > 1) params.set("page", String(page));
      if (pagination.pageSize !== DEFAULT_PAGE_SIZE) {
        params.set("pageSize", String(pagination.pageSize));
      }

      const isDefaultSort =
        sorted.length === 1 &&
        sorted[0].id === DEFAULT_SORT[0].id &&
        sorted[0].desc === DEFAULT_SORT[0].desc;
      if (!isDefaultSort) {
        params.set(
          "sort",
          sorted.length
            ? sorted.map(({ id, desc }) => `${desc ? "-" : ""}${id}`).join(",")
            : "none"
        );
      }

      const trimmedSearch = searchValue.trim();
      if (trimmedSearch) params.set("q", trimmedSearch);
      if (filterValue?.organizationType) {
        params.set("type", filterValue.organizationType);
      }
      if (filterValue?.flaggedStatus === "Flagged") {
        params.set("status", "flagged");
      }

      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions: any = [];

      if (filterValue?.organizationType) {
        andConditions.push({
          organizationType: filterValue.organizationType,
        });
      }

      if (filterValue?.flaggedStatus) {
        if (filterValue.flaggedStatus === "Flagged") {
          andConditions.push({
            $or: [
              {
                semgMentions: {
                  id: {
                    $notNull: true,
                  },
                },
              },

              {
                performanceRatings: {
                  $and: [
                    {
                      isActive: true,
                    },
                    {
                      performanceRatingType: {
                        isFlag: true,
                      },
                    },
                  ],
                },
              },
              {
                performanceRatings: {
                  $and: [
                    {
                      performanceRatingType: {
                        isDebarred: true,
                      },
                    },
                    {
                      isActive: true,
                    },
                  ],
                },
              },
            ],
          });
        } else if (filterValue.flaggedStatus === "Not Flagged") {
          andConditions.push({
            $and: [
              {
                semgMentions: {
                  id: {
                    $null: true, // Check if `semgMentions` is null (i.e., no records exist)
                  },
                },
              },
              {
                $or: [
                  {
                    performanceRatings: {
                      id: {
                        $null: true, // Check if no performance ratings exist
                      },
                    },
                  },
                  {
                    performanceRatings: {
                      performanceRatingType: {
                        isFlag: {
                          $ne: true,
                        }, // Check if performance rating exists and `isFlag` is false
                      },
                    },
                  },
                  {
                    performanceRatings: {
                      performanceRatingType: {
                        isDebarred: {
                          $ne: true,
                        },
                      },
                    },
                  },
                ],
              },
            ],
          });
        }
      }

      andConditions.push({
        $or: [
          {
            contractorName: {
              $containsi: searchValue,
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          fields: ["contractorName", "acronym", "rmuFlag", "updatedAt"],
          populate: {
            organizationType: "name",
            contracts: {
              count: true,
            },
            semgMentions: {
              count: true,
            },
            capacityAssessments: {
              count: true,
            },
            riskAssessments: {
              count: true,
            },
            pvrs: {
              count: true,
            },
            performanceRatings: {
              populate: {
                performanceRatingType: true,
              },
            },
          },
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getContractors(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {
          autoClose: 500,
        });
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams, unCheckAll]
  );

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [filterValue, onPageChange, pagination.pageIndex, searchValue, sorted]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteContractor(selectedRecord);
      } catch (e: any) {
        toast.error("Error while deleting data", {});
        toast.success("Contractor deleted successfully", {});
      } finally {
        setDeleting(false);
        fetchUpdatedData();
        setDeleteModal(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });

    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deleteContractor({ id });
          })
        );
        setDeleting(false);
        toast.success("Contractors deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "contractor",
        action: "delete",
      },
    ]),
  });
  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Contractor Name",
        accessorKey: "contractorName",
        id: "contractorName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{ whiteSpace: "pre-wrap", width: "300px" }}
              >
                <div className="flex-shrink-0 avatar-xs me-3">
                  <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-16">
                    <i className={"ri-building-line"}></i>
                  </div>
                </div>
                <Link
                  to={`/contractors/${rowData.id}`}
                  className="fw-medium link-primary"
                >
                  {cell.getValue()}
                </Link>
              </div>
            </>
          );
        },
      },
      {
        header: "Organization Type",
        accessorKey: "organizationType.name",
        enableColumnFilter: false,
        id: "organizationType.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "-"}</>;
        },
      },
      {
        header: "Flag Status",
        accessorKey: "flaggedStatus",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const statuses = [];
          const rowData = cell?.row?.original;

          if (rowData?.semgMentions && rowData?.semgMentions?.count > 0) {
            statuses.push(<SEMGMention />);
            statuses.push(<br />);
          }
          if (
            rowData?.performanceRatings?.some(
              (rating: any) =>
                rating?.isActive &&
                (rating?.performanceRatingType?.isFlag ||
                  rating?.performanceRatingType?.isDebarred)
            )
          ) {
            statuses.push(<Flagged />);
          }
          if (statuses.length === 0) {
            statuses.push(
              <span className="badge bg-primary-subtle text-primary fs-11 me-2">
                N/A
              </span>
            );
          }

          return <>{statuses}</>;
        },
      },
      {
        header: "Contracts",
        accessorKey: "contracts.count",
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        header: "Assessments",
        accessorKey: "assessments",
        enableColumnFilter: false,
        id: "capacityAssessments",
        enableSorting: false,
        sortingFn: () => null,
        cell: (cell: any) => {
          const rowData = cell?.row?.original;
          return (
            (rowData?.capacityAssessments?.count ?? 0) +
            (rowData?.riskAssessments?.count ?? 0) +
            (rowData?.pvrs?.count ?? 0)
          );
        },
      },
      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps?.row?.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="contractor" action="findOne">
                  <DropdownItem
                    href={`#`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/contractors/${rowData?.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="contractor" action="mergeContractors">
                  <DropdownItem
                    href={`#`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/contractors/merge/${rowData?.id}`);
                    }}
                  >
                    <i className="ri-arrow-left-right-line align-bottom me-2 text-muted"></i>{" "}
                    Merge
                  </DropdownItem>
                </Permission>

                <Permission resource="contractor" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );
  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => handleDelete()}
        onCloseClick={() => setDeleteModal(false)}
        loading={deleting}
      />
      <DeleteModal
        show={deleteMultipleModal}
        onDeleteClick={() => handleDeleteMultiple()}
        onCloseClick={() => setDeleteMultipleModal(false)}
        loading={deleting}
      />
      <Permissions checks={[{ resource: "contractor", action: "delete" }]}>
        <BulkActionBar
          count={selectedCheckBox.length}
          onClear={unCheckAll}
          itemLabel="contractor"
        >
          <Button
            color="danger"
            size="sm"
            className="material-shadow-none"
            onClick={() => setDeleteMultipleModal(true)}
          >
            <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
          </Button>
        </BulkActionBar>
      </Permissions>
      <Card>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col xxl={5} lg={6}>
              <FilterSearchBox
                id="contractor-search"
                placeholder="Search contractors..."
                value={searchValue}
                onChange={(value: string) => {
                  setSearchValue(value);
                  setPage(1);
                }}
              />
            </Col>
            <Col xxl={4} lg={6}>
              <FilterLabel htmlFor="organizationTypeFilter">
                Organization Type
              </FilterLabel>
              <SelectOrganizationTypeFilter
                onChange={(value) => {
                  setFilterValue({ ...filterValue, organizationType: value });
                  setPage(1);
                }}
                value={filterValue.organizationType}
              />
            </Col>
            <Col xxl={3} lg={6}>
              <FilterValueSelect
                id="flaggedStatusFilter"
                label="Status"
                placeholder="Select Status"
                value={filterValue.flaggedStatus}
                onChange={(value: any) => {
                  setFilterValue({ ...filterValue, flaggedStatus: value });
                  setPage(1);
                }}
                options={[{ value: "Flagged", label: "Flagged" }]}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={8}>
              <h5 className="card-title mb-1">Contractors</h5>
              <p className="mb-0 text-muted">
                This is a comprehensive list of contractors, vendors, and
                implementing partners provided by various UN agencies.
              </p>
            </Col>
            <div className="col-md-auto ms-auto">
              <div className="d-flex gap-2">
                <Permission resource="contractor" action="create">
                  <Link to="/contractors/add" className="btn btn-primary">
                    <i className="ri-add-line align-bottom me-1"></i>
                    Add Contractor
                  </Link>
                </Permission>

                <Permissions
                  checks={[
                    {
                      resource: "contractor",
                      action: "import",
                    },
                    {
                      resource: "contractor",
                      action: "export",
                    },
                  ]}
                >
                  <Import apiParams={apiParams} onImported={fetchUpdatedData} />
                </Permissions>
              </div>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          <TableContainer
            loading={loading}
            columns={columns}
            data={data || []}
            pagination={pagination}
            setPagination={setPagination}
            sorted={sorted}
            setSorted={setSorted}
            onPageChange={onPageChange}
            FilterSection={() => {}}
            totalRows={meta?.pagination?.total || 0}
            totalPages={meta?.pagination?.pageCount || 0}
            searchValue={searchValue}
            filterValue={filterValue}
            columnVisibility={columnVisibility}
            divClass="table-responsive table-card mb-4"
            tableClass="align-middle table-nowrap mb-0 table-striped"
            theadClass="table-light table-nowrap"
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default AllContractors;
